// src/pages/dashboards/components/WidgetRenderer.tsx
import { Card } from '@/components/ui';
import { TrendingUp, TrendingDown, Minus, BarChart3, PieChart, LineChart, List, Table2, Gauge } from 'lucide-react';
import { TipoWidget } from '@/types/reporte';
import type { DashboardWidget } from '@/types/reporte';

interface Props {
  widget: DashboardWidget;
}

/**
 * Renderiza un widget según su tipo.
 * Los widgets de gráficos muestran placeholders con los valores.
 */
export function WidgetRenderer({ widget }: Props) {
  const { tipo_widget, titulo, datos } = widget;

  // Obtener valor y tendencia
  const valor = datos?.valor ?? 0;
  const valorFormateado = datos?.valor_formateado ?? String(valor);
  const tendencia = datos?.tendencia ?? 0;
  const direccion = datos?.tendencia_direccion ?? 'stable';

  const renderTendencia = () => {
    if (direccion === 'up') {
      return (
        <div className="flex items-center text-green-600 text-sm">
          <TrendingUp className="h-4 w-4 mr-1" />
          <span>+{tendencia.toFixed(1)}%</span>
        </div>
      );
    }
    if (direccion === 'down') {
      return (
        <div className="flex items-center text-red-600 text-sm">
          <TrendingDown className="h-4 w-4 mr-1" />
          <span>{tendencia.toFixed(1)}%</span>
        </div>
      );
    }
    return (
      <div className="flex items-center text-gray-500 text-sm">
        <Minus className="h-4 w-4 mr-1" />
        <span>0%</span>
      </div>
    );
  };

  switch (tipo_widget) {
    case TipoWidget.CONTADOR:
      return (
        <Card className="p-4">
          <div className="flex flex-col">
            <span className="text-sm text-gray-500 mb-1">{titulo}</span>
            <div className="flex items-end justify-between">
              <span className="text-3xl font-bold">{valorFormateado}</span>
              {renderTendencia()}
            </div>
          </div>
        </Card>
      );

    case TipoWidget.GRAFICO_LINEA:
      return (
        <Card className="p-4">
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-700">{titulo}</span>
              <LineChart className="h-5 w-5 text-blue-500" />
            </div>
            <div className="flex-1 flex items-center justify-center min-h-[120px] bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
              <div className="text-center">
                <LineChart className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Grafico de Linea</p>
                <p className="text-lg font-semibold text-gray-700">{valorFormateado}</p>
              </div>
            </div>
          </div>
        </Card>
      );

    case TipoWidget.GRAFICO_BARRA:
      return (
        <Card className="p-4">
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-700">{titulo}</span>
              <BarChart3 className="h-5 w-5 text-green-500" />
            </div>
            <div className="flex-1 flex items-center justify-center min-h-[120px] bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
              <div className="text-center">
                <BarChart3 className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Grafico de Barras</p>
                <p className="text-lg font-semibold text-gray-700">{valorFormateado}</p>
              </div>
            </div>
          </div>
        </Card>
      );

    case TipoWidget.GRAFICO_PIE:
      return (
        <Card className="p-4">
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-700">{titulo}</span>
              <PieChart className="h-5 w-5 text-purple-500" />
            </div>
            <div className="flex-1 flex items-center justify-center min-h-[120px] bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
              <div className="text-center">
                <PieChart className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Grafico Pie</p>
                <p className="text-lg font-semibold text-gray-700">{valorFormateado}</p>
              </div>
            </div>
          </div>
        </Card>
      );

    case TipoWidget.GRAFICO_DONA:
      return (
        <Card className="p-4">
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-700">{titulo}</span>
              <PieChart className="h-5 w-5 text-orange-500" />
            </div>
            <div className="flex-1 flex items-center justify-center min-h-[120px] bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
              <div className="text-center">
                <div className="relative">
                  <PieChart className="h-12 w-12 text-gray-400 mx-auto" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="h-6 w-6 bg-white rounded-full" />
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-2">Grafico Dona</p>
                <p className="text-lg font-semibold text-gray-700">{valorFormateado}</p>
              </div>
            </div>
          </div>
        </Card>
      );

    case TipoWidget.TABLA:
      return (
        <Card className="p-4">
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-700">{titulo}</span>
              <Table2 className="h-5 w-5 text-gray-500" />
            </div>
            <div className="flex-1 overflow-auto">
              {Array.isArray(valor) && valor.length > 0 ? (
                <table className="w-full text-sm">
                  <tbody>
                    {valor.slice(0, 5).map((item, idx) => (
                      <tr key={idx} className="border-b last:border-0">
                        <td className="py-2 text-gray-700">
                          {typeof item === 'object' ? JSON.stringify(item) : String(item)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="flex items-center justify-center min-h-[80px] text-gray-500">
                  Sin datos
                </div>
              )}
            </div>
          </div>
        </Card>
      );

    case TipoWidget.LISTA:
      return (
        <Card className="p-4">
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-700">{titulo}</span>
              <List className="h-5 w-5 text-gray-500" />
            </div>
            <div className="flex-1 overflow-auto">
              {Array.isArray(valor) && valor.length > 0 ? (
                <ul className="space-y-2">
                  {valor.slice(0, 5).map((item, idx) => (
                    <li key={idx} className="flex items-center text-sm">
                      <span className="h-2 w-2 bg-blue-500 rounded-full mr-2" />
                      <span className="text-gray-700">
                        {typeof item === 'object' ? JSON.stringify(item) : String(item)}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="flex items-center justify-center min-h-[80px] text-gray-500">
                  Sin datos
                </div>
              )}
            </div>
          </div>
        </Card>
      );

    case TipoWidget.PROGRESO:
      const porcentaje = typeof valor === 'number' ? Math.min(100, Math.max(0, valor)) : 0;
      return (
        <Card className="p-4">
          <div className="flex flex-col">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">{titulo}</span>
              <Gauge className="h-5 w-5 text-blue-500" />
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-1 h-4 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 transition-all duration-300"
                  style={{ width: `${porcentaje}%` }}
                />
              </div>
              <span className="text-lg font-semibold min-w-[60px] text-right">
                {valorFormateado}
              </span>
            </div>
          </div>
        </Card>
      );

    case TipoWidget.TENDENCIA:
      return (
        <Card className="p-4">
          <div className="flex flex-col">
            <span className="text-sm text-gray-500 mb-1">{titulo}</span>
            <div className="flex items-center gap-3">
              {direccion === 'up' ? (
                <div className="flex items-center text-green-600">
                  <TrendingUp className="h-8 w-8" />
                  <span className="text-2xl font-bold ml-2">+{tendencia.toFixed(1)}%</span>
                </div>
              ) : direccion === 'down' ? (
                <div className="flex items-center text-red-600">
                  <TrendingDown className="h-8 w-8" />
                  <span className="text-2xl font-bold ml-2">{tendencia.toFixed(1)}%</span>
                </div>
              ) : (
                <div className="flex items-center text-gray-500">
                  <Minus className="h-8 w-8" />
                  <span className="text-2xl font-bold ml-2">0%</span>
                </div>
              )}
            </div>
            <span className="text-sm text-gray-500 mt-1">Valor actual: {valorFormateado}</span>
          </div>
        </Card>
      );

    default:
      return (
        <Card className="p-4">
          <div className="flex items-center justify-center min-h-[100px] text-gray-500">
            Widget tipo desconocido: {tipo_widget}
          </div>
        </Card>
      );
  }
}
